import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const Reorder = ({ label, note, items, labels, onChange, onSave, testId }) => (
  <div className="bg-white border border-line px-6 md:px-8 py-7" data-testid={testId}>
    <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-2">{label}</div>
    <p className="text-[12.5px] font-light text-[#6E6A63] mb-5 leading-relaxed">{note}</p>
    <div className="border-t border-line">
      {items.map((slug, i) => (
        <div key={slug} className="flex items-center justify-between gap-4 py-3.5 border-b border-line">
          <div className="flex items-center gap-4 min-w-0">
            <span className="display text-[17px] text-sage/70 w-6">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-[13.5px] text-charcoal truncate">{labels[slug] || slug}</span>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button
              onClick={() => onChange(move(items, i, -1))}
              data-testid={`${testId}-up-${slug}`}
              className="p-2 border border-line hover:border-sage text-[#7A766F] hover:text-sage transition-colors"
            >
              <ArrowUp size={13} />
            </button>
            <button
              onClick={() => onChange(move(items, i, 1))}
              data-testid={`${testId}-down-${slug}`}
              className="p-2 border border-line hover:border-sage text-[#7A766F] hover:text-sage transition-colors"
            >
              <ArrowDown size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
    <button
      onClick={onSave}
      data-testid={`${testId}-save`}
      className="mt-6 px-7 py-3 bg-charcoal text-white text-[11.5px] tracking-[0.14em] uppercase hover:bg-sage transition-colors"
    >
      Save order
    </button>
  </div>
);

export default function MerchandisingAdmin() {
  const [settings, setSettings] = useState(null);
  const [tools, setTools] = useState([]);
  const [articles, setArticles] = useState([]);
  const [toolOrder, setToolOrder] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/tools"),
      api.get("/admin/articles"),
    ]).then(([s, t, a]) => {
      setSettings(s.data);
      setTools(t.data);
      setArticles(a.data);
      setToolOrder(s.data.tool_order || []);
      setFeatured(s.data.featured_articles || []);
      setQuestions(s.data.homepage_questions || []);
    });
  }, []);

  const save = async (body) => {
    try {
      const { data } = await api.patch("/admin/settings", body);
      setSettings(data);
      toast.success("Merchandising updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save changes");
    }
  };

  if (!settings) return <AdminPage title="Merchandising" />;

  const toolLabels = Object.fromEntries(tools.map((t) => [t.slug, t.name]));
  const articleLabels = Object.fromEntries(articles.map((a) => [a.slug, a.title]));
  const publishable = articles.filter((a) => a.status === "PUBLISHED");

  return (
    <AdminPage
      title="Merchandising"
      lede="Profitability determines prominence, never the recommendation. Ordering here controls what appears first on the homepage; tool results and product suitability are unaffected."
    >
      <div className="grid lg:grid-cols-2 gap-8">
        <Reorder
          label="Homepage planning tool order"
          note="Initial order is set for commercial priority. Once real performance data exists, reorder based on revenue per tool completion."
          items={toolOrder}
          labels={toolLabels}
          onChange={setToolOrder}
          onSave={() => save({ tool_order: toolOrder })}
          testId="reorder-tools"
        />
        <Reorder
          label="Popular home decisions (featured guides)"
          note="Strategic importance now, commercial performance later."
          items={featured}
          labels={articleLabels}
          onChange={setFeatured}
          onSave={() => save({ featured_articles: featured })}
          testId="reorder-featured"
        />
        <Reorder
          label="Homepage decision questions"
          note="The 'Start with the question, not the product' list."
          items={questions}
          labels={articleLabels}
          onChange={setQuestions}
          onSave={() => save({ homepage_questions: questions })}
          testId="reorder-questions"
        />

        <div className="bg-white border border-line px-6 md:px-8 py-7" data-testid="site-mode-panel">
          <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-5">Site mode</div>
          <label className="block mb-5">
            <span className="text-[12px] font-light text-[#5A5A5A]">Mode</span>
            <select
              value={settings.site_mode}
              onChange={(e) => save({ site_mode: e.target.value })}
              data-testid="site-mode-select"
              className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
            >
              <option value="preview">preview</option>
              <option value="live">live</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[12px] font-light text-[#5A5A5A]">Preview banner text</span>
            <textarea
              rows={3}
              defaultValue={settings.preview_banner}
              onBlur={(e) => save({ preview_banner: e.target.value })}
              data-testid="preview-banner-input"
              className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white focus:outline-none focus:border-sage"
            />
          </label>
          <p className="text-[12px] font-light text-[#6E6A63] mt-4 leading-relaxed">
            Leave the banner empty to remove it. The production domain is not connected — changing mode
            here does not affect humanhomeliving.com.
          </p>
        </div>

        <div className="bg-white border border-line px-6 md:px-8 py-7 lg:col-span-2" data-testid="available-slugs">
          <div className="text-[10px] tracking-[0.2em] uppercase text-sage mb-4">
            Published guides available for featuring
          </div>
          <div className="flex flex-wrap gap-2">
            {publishable.map((a) => (
              <span key={a.slug} className="text-[11.5px] font-light px-3 py-1.5 bg-bone border border-line text-[#5A5A5A]">
                {a.slug}
              </span>
            ))}
          </div>
        </div>
      </div>
    </AdminPage>
  );
}
